import {
    AppUser,
    combineChildTimeAndParent,
    EventrDate,
    filterEntitiesToPastAndPresentWP,
    formatTime,
    getV2ValueWP,
    IEventrHotel,
    INextComponent,
    IRendererOptions,
    isDateInPast,
    ITodoList,
    structuredCloneL,
    TPastCriteriaJoiner,
    tryParseJSON,
    TZoneTypes,
    V2,
} from '@eventr/common'

/**
 * Compare two dates to see if same?
 * returns false for if they are the same
 * returns true for if they are not the same
 */
const notSameDate = (
    date1: EventrDate,
    date2: EventrDate,
    zoneType: TZoneTypes
) => {
    if (!date1 || !date2) return false

    switch (zoneType) {
        case 'userLocal':
            return (
                date1.toUserLocalDateString() !== date2.toUserLocalDateString()
            )
        case 'itinLocal':
            return (
                date1.toItinLocalDateString() !== date2.toItinLocalDateString()
            )
        case 'utc':
            return (
                date1.utcLuxon?.toISO()?.slice(0, 10) !==
                date2.utcLuxon?.toISO()?.slice(0, 10)
            )
    }
}

export const getDateTimeISOFromEventrDateStringAndZone = (
    dateString: string,
    zoneType: TZoneTypes
) => {
    const date = new EventrDate(dateString)

    switch (zoneType) {
        case 'userLocal':
            return date.toUserLocalDateTimeString()
        case 'itinLocal':
            return date.toItinLocalDateTimeString()
        case 'utc':
            return date.dateTimeString
        default:
            return date.toUserLocalDateTimeString()
    }
}

const getDateRanges = (start: EventrDate, end: EventrDate) => {
    const dates: EventrDate[] = []
    const startDate = start.getCloneEventrDate()
    const endDate = end.getCloneEventrDate()

    for (
        const d = start.getCloneEventrDate();
        d.toItinLocalDateString() < endDate.toItinLocalDateString();
        d.add({ days: 1 })
    ) {
        if (d.toItinLocalDateString() !== startDate.toItinLocalDateString()) {
            const date = d.getCloneEventrDate()
            d.setFromObject({ hour: 0, minute: 0 })
            dates.push(date)
        }
    }

    return dates
}

export const generateUpNextList = (
    wpUser: AppUser,
    data: any[],
    options: IRendererOptions,
    screenType: string,
    zoneType: TZoneTypes,
    sectionData?: V2,
    timeFormat?: string
) => {
    if (wpUser) {
        //filter out to do lists
        const todos = []
        if (sectionData?.sectionID === 8) {
            todos.push(...data.filter((item) => item.type === 'todo'))
            data = data.filter(
                (item) => item.type !== 'todo' && item.type !== 'stand'
            )
        }

        const sortedPastPresent = filterEntitiesToPastAndPresentWP(
            data,
            wpUser,
            options.settings,
            screenType
        )

        /// only: 'past' | 'present' is to filter out the past or present as filterEntitiesToPastAndPresentWP return multiple
        const generateComponents = (list: any[], only?: 'past' | 'present') => {
            /// gives past and present entries
            const comps: INextComponent[] = []
            const entitiesWithChildren = []

            /// if every option setting has a child entity
            if (options.settings.childEntity) {
                /// for every entry in the list
                list.forEach((p) => {
                    const children = JSON.parse(
                        getV2ValueWP(options.settings.childEntity ?? '', p)
                    )
                    /// for every children in list
                    children.forEach((c: any) => {
                        const pCopy = { ...p }
                        pCopy[options.settings.childEntity ?? ''] = c
                        entitiesWithChildren.push(pCopy)
                    })
                })
            } else {
                /// otherwise push the list
                entitiesWithChildren.push(...list)
            }

            /// builds list of events
            entitiesWithChildren.forEach((p) => {
                let booking

                if (options.settings.multiBooking) {
                    if (sectionData?.sectionID === 3) {
                        booking = hotelGetBooking(p, wpUser.id)
                    } else {
                        // TODO check if multi booking is only for hotel if so remove this
                        booking = tryParseJSON(
                            getV2ValueWP(
                                options.settings.multiBooking.locator,
                                p,
                                wpUser
                            ),
                            []
                        ).find((el: any) => {
                            return el._id === p.identifier
                        })
                    }
                }

                let time = booking
                    ? booking[options.settings.multiBooking.primaryCard]
                    : getV2ValueWP(
                          options.settings.pastCriteria ?? '',
                          p,
                          wpUser as any
                      )

                let time2 = booking
                    ? booking[options.settings.multiBooking.secondaryCard]
                    : getV2ValueWP(
                          options.settings.secondaryCard ?? '',
                          p,
                          wpUser as any
                      )

                const checkIn = getV2ValueWP('check_in', p, wpUser as any)
                if (options.settings.childEntityPastCriteria) {
                    const childTime = getV2ValueWP(
                        options.settings.childEntityPastCriteria ?? '',
                        p
                    )
                    if (childTime !== 'null') {
                        time = combineChildTimeAndParent(
                            time,
                            childTime,
                            options.settings
                                .pastCriteriaJoiner as TPastCriteriaJoiner
                        )
                        time2 = combineChildTimeAndParent(
                            time2,
                            childTime,
                            options.settings
                                .pastCriteriaJoiner as TPastCriteriaJoiner
                        )
                    }
                }

                const isDiffDate = notSameDate(
                    new EventrDate(time),
                    new EventrDate(time2),
                    zoneType
                )

                const ongoing = options.settings.trackLive
                    ? !isDateInPast(time) && isDateInPast(time2)
                    : false

                if (time !== 'null') {
                    let selectedTime = time
                    if (!isDiffDate && !isDateInPast(time2)) {
                        selectedTime = time2
                    }
                    if (checkIn && !isDiffDate && !isDateInPast(checkIn)) {
                        selectedTime = checkIn
                    }

                    comps.push({
                        date: getDateTimeISOFromEventrDateStringAndZone(
                            selectedTime,
                            zoneType
                        ),
                        sortDate: selectedTime,
                        data: {
                            ...p,
                            label: `${
                                ongoing
                                    ? 'Live'
                                    : isDiffDate || isDateInPast(time2)
                                    ? options.settings.primaryLabel
                                    : checkIn && !isDateInPast(checkIn)
                                    ? 'Check-in'
                                    : options.settings.secondaryLabel
                            } ${formatTime(
                                selectedTime,
                                zoneType,
                                timeFormat
                            )}`,
                            live: ongoing,
                            labelTime: selectedTime,
                            startOrEnd: selectedTime === time ? 'end' : 'start',
                        },
                        renderOptions: sectionData ? sectionData : options,
                    })
                }

                if (options.settings.secondaryCard && isDiffDate) {
                    const timeToUse =
                        checkIn && !isDateInPast(checkIn) ? checkIn : time2
                    comps.push({
                        date: getDateTimeISOFromEventrDateStringAndZone(
                            time2,
                            zoneType
                        ),
                        sortDate: time2,
                        data: {
                            ...p,
                            label: `${
                                checkIn && !isDateInPast(checkIn)
                                    ? 'Check-in'
                                    : options.settings.secondaryLabel
                            } ${formatTime(timeToUse, zoneType, timeFormat)}`,
                            labelTime: timeToUse,
                            startOrEnd: 'start',
                        },
                        renderOptions: sectionData ? sectionData : options,
                    })
                }

                if (options.settings.multiDays && isDiffDate) {
                    const dates = getDateRanges(
                        new EventrDate(time2),
                        new EventrDate(time)
                    )

                    dates.forEach((date) => {
                        comps.push({
                            date: date.dateTimeString,
                            sortDate: date.dateTimeString,
                            data: {
                                ...p,
                                subCard: true,
                                multiDays: true,
                            },
                            renderOptions: sectionData ? sectionData : options,
                        })
                    })
                }
            })

            /// Comps list to push all present events
            const tempList: INextComponent[] = []
            const currentDate = new EventrDate()
            comps.forEach((c) => {
                const pastDate = isDateInPast(c.sortDate)
                if (
                    only === 'present'
                        ? !pastDate ||
                          (!notSameDate(
                              new EventrDate(c.sortDate),
                              currentDate,
                              zoneType
                          ) &&
                              c.data.subCard)
                        : pastDate
                ) {
                    tempList.push(c)
                }
            })
            return tempList
        }

        const generatedComponents: {
            present: INextComponent[]
            past: INextComponent[]
            todos: ITodoList[]
        } = { present: [], past: [], todos: todos }

        generatedComponents.present = generateComponents(
            sortedPastPresent.present,
            'present'
        )

        generatedComponents.past = generateComponents(
            sortedPastPresent.past,
            'past'
        )
        return generatedComponents
    }
}

const hotelGetBooking = (hotel: IEventrHotel, userId: number) => {
    let booking = hotel.rooms?.find((b) =>
        b.guests?.some((g) => {
            return g.option === userId
        })
    )
    if (!booking) {
        return null
    }
    const c_booking = structuredCloneL(booking)
    if (!c_booking?.override) {
        if (hotel.defualtCheckIn && hotel.defualtCheckOut) {
            c_booking.checkIn = hotel.defualtCheckIn
            c_booking.checkOut = hotel.defualtCheckOut
        }
    }

    return c_booking
}
